/**
 * Authenticated fetch: adds JWT, on 401 tries refresh then retries once.
 * Refresh-token failures (403 Token Revoked) clear session via authService.
 */

import { getAuthToken, refreshAccessToken } from './authService';

// Re-export for services that only need getAuthToken
export { getAuthToken } from './authService';

/**
 * Fetch with Authorization header. On 401, calls refresh-token API, stores new tokens, retries once.
 * If refresh fails (403 revoked / invalid refresh token), session is cleared and user is signed out.
 * @param {string} url - Full URL
 * @param {RequestInit} options - fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>}
 */
export async function fetchWithAuth(url, options = {}) {
  const token = getAuthToken();
  const isFormData =
    typeof FormData !== 'undefined' &&
    options &&
    options.body &&
    options.body instanceof FormData;
  const headers = {
    // Don't set Content-Type for FormData (browser will set boundary)
    ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers || {}),
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    await refreshAccessToken();
    const newToken = getAuthToken();
    const retryHeaders = {
      ...headers,
      ...(newToken && { Authorization: `Bearer ${newToken}` }),
    };
    response = await fetch(url, { ...options, headers: retryHeaders });
  }

  return response;
}
